import { Injectable } from '@angular/core';
import {
  TemporalFinancialAmount,
  ValidationResult,
  DataQualityResult
} from '@shared/definitions/unified-financial-data.model';
import { DateLike, toDate } from '@shared/utils/to-date';

/**
 * Structural input to `calculateDataQuality`. Every field is optional
 * because the quality score is computed by *missing-field* penalties —
 * callers pass whatever they have and the service decides the gaps.
 */
export interface DataQualityInput {
  baseRent?: { amount?: number; source?: 'actual' | 'estimated' | string };
  currentValue?: { amount?: number };
  propertyTax?: { amount?: number };
  insurance?: { amount?: number };
  rentGrowthRate?: number | null;
  /**
   * Accepts every date shape our Firestore reads produce. See the
   * shared `DateLike` type for the full union (`Date` / ISO string /
   * epoch number / Firestore Timestamp / `{ seconds }`).
   */
  lastUpdated?: DateLike | null;
}

/**
 * DATA VALIDATION SERVICE
 * 
 * Handles validation, normalization, and quality assessment of financial data.
 * Ensures temporal consistency and proper data quality scoring.
 * 
 * Key responsibilities:
 * - Validate temporal financial amounts
 * - Convert between time periods (monthly/quarterly/annually)
 * - Calculate data quality and confidence scores
 * - Provide normalization and standardization
 */
@Injectable({
  providedIn: 'root'
})
export class DataValidationService {

  private readonly SUPPORTED_CURRENCIES = ['EUR', 'USD', 'GBP', 'CHF'];
  private readonly CONVERSION_MULTIPLIERS = {
    'monthly': { 'quarterly': 3, 'annually': 12 },
    'quarterly': { 'monthly': 1/3, 'annually': 4 },
    'annually': { 'monthly': 1/12, 'quarterly': 1/4 }
  };

  /**
   * Validate temporal financial amount
   */
  validateTemporalAmount(amount: TemporalFinancialAmount): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    
    // Amount validation
    if (amount.amount < 0) {
      errors.push('Amount cannot be negative');
    }
    
    if (!Number.isFinite(amount.amount)) {
      errors.push('Amount must be a valid number');
    }

    if (amount.amount === 0) {
      warnings.push('Amount is zero - consider if this is intentional');
    }
    
    // Period validation
    if (!['monthly', 'quarterly', 'annually'].includes(amount.period)) {
      errors.push('Invalid time period. Must be monthly, quarterly, or annually');
    }
    
    // Currency validation
    if (!amount.currency || amount.currency.length !== 3) {
      errors.push('Invalid currency code. Must be 3-character ISO code');
    }

    if (!this.SUPPORTED_CURRENCIES.includes(amount.currency)) {
      warnings.push(`Currency ${amount.currency} is not in supported list: ${this.SUPPORTED_CURRENCIES.join(', ')}`);
    }
    
    // Confidence validation
    if (amount.confidence < 0 || amount.confidence > 1) {
      errors.push('Confidence must be between 0 and 1');
    }

    if (amount.confidence < 0.5) {
      warnings.push('Low confidence data detected - consider data quality improvement');
    }

    // Source validation
    if (!['actual', 'estimated', 'projected'].includes(amount.source)) {
      errors.push('Invalid source. Must be actual, estimated, or projected');
    }

    // Date validation
    if (!amount.effectiveDate || isNaN(amount.effectiveDate.getTime())) {
      errors.push('Invalid effective date');
    }

    const normalized = errors.length === 0 ? this.normalizeAmount(amount) : undefined;
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      normalizedData: normalized
    };
  }
  
  /**
   * Convert temporal amount to monthly equivalent
   */
  convertToMonthly(amount: TemporalFinancialAmount): TemporalFinancialAmount {
    if (amount.period === 'monthly') {
      return { ...amount };
    }

    const multiplier = amount.period === 'quarterly' ? 1/3 : 1/12;
    
    return {
      ...amount,
      amount: amount.amount * multiplier,
      period: 'monthly'
    };
  }
  
  /**
   * Convert temporal amount to annual equivalent
   */
  convertToAnnual(amount: TemporalFinancialAmount): TemporalFinancialAmount {
    if (amount.period === 'annually') {
      return { ...amount };
    }

    const multiplier = amount.period === 'monthly' ? 12 : 4;
    
    return {
      ...amount,
      amount: amount.amount * multiplier,
      period: 'annually'
    };
  }

  /**
   * Convert between any two periods
   */
  convertPeriod(
    amount: TemporalFinancialAmount, 
    targetPeriod: 'monthly' | 'quarterly' | 'annually'
  ): TemporalFinancialAmount {
    if (amount.period === targetPeriod) {
      return { ...amount };
    }

    let convertedAmount: number;

    // Convert to annual first as base
    if (amount.period === 'monthly') {
      convertedAmount = amount.amount * 12;
    } else if (amount.period === 'quarterly') {
      convertedAmount = amount.amount * 4;
    } else {
      convertedAmount = amount.amount;
    }

    // Then convert to target period
    if (targetPeriod === 'monthly') {
      convertedAmount = convertedAmount / 12;
    } else if (targetPeriod === 'quarterly') {
      convertedAmount = convertedAmount / 4;
    }

    return {
      ...amount,
      amount: convertedAmount,
      period: targetPeriod
    };
  }
  
  /**
   * Validate data quality and calculate confidence score
   */
  calculateDataQuality(data: DataQualityInput): DataQualityResult {
    let score = 1.0;
    const issues: string[] = [];
    const confidenceFactors: string[] = [];
    
    // Check for missing required fields
    if (!data.baseRent || data.baseRent.amount === 0) {
      score -= 0.3;
      issues.push('Missing or zero rental income');
    } else {
      confidenceFactors.push('Rental income available');
    }
    
    // Check for estimated vs actual data
    if (data.baseRent?.source === 'estimated') {
      score -= 0.2;
      issues.push('Rental income is estimated rather than actual');
    } else if (data.baseRent?.source === 'actual') {
      confidenceFactors.push('Actual rental income data');
    }

    // Check property value availability
    if (!data.currentValue || data.currentValue.amount === 0) {
      score -= 0.2;
      issues.push('Missing property valuation');
    } else {
      confidenceFactors.push('Property valuation available');
    }
    
    // Check data freshness - handle various date formats safely
    if (data.lastUpdated) {
      const lastUpdatedDate = toDate(data.lastUpdated);

      if (!lastUpdatedDate) {
        console.warn('[DataValidationService] Failed to parse lastUpdated date:', data.lastUpdated);
        issues.push('Unable to determine data freshness');
        score -= 0.05;
      } else {
        const daysSinceUpdate = (Date.now() - lastUpdatedDate.getTime()) / (1000 * 60 * 60 * 24);
        if (daysSinceUpdate > 90) {
          score -= 0.1;
          issues.push('Data is more than 90 days old');
        } else if (daysSinceUpdate < 30) {
          confidenceFactors.push('Recent data (less than 30 days old)');
        }
      }
    }

    // Check for operating expense data
    if ((data.propertyTax?.amount ?? 0) > 0) {
      confidenceFactors.push('Property tax data available');
    } else {
      issues.push('Missing property tax information');
      score -= 0.05;
    }

    if ((data.insurance?.amount ?? 0) > 0) {
      confidenceFactors.push('Insurance cost data available');
    } else {
      issues.push('Missing insurance cost information');
      score -= 0.05;
    }

    // Check for growth rate assumptions
    if (data.rentGrowthRate !== undefined && data.rentGrowthRate !== null) {
      confidenceFactors.push('Rent growth rate defined');
    } else {
      issues.push('No rent growth rate specified');
      score -= 0.05;
    }

    // Ensure score doesn't go below 0
    score = Math.max(0, score);

    const quality = score > 0.8 ? 'high' : score > 0.5 ? 'medium' : 'low';
    
    return {
      score,
      quality,
      issues,
      recommendations: this.generateRecommendations(issues),
      confidenceFactors
    };
  }

  /**
   * Normalize amount to standard format
   */
  private normalizeAmount(amount: TemporalFinancialAmount): TemporalFinancialAmount {
    return {
      ...amount,
      amount: Math.round(amount.amount * 100) / 100, // Round to 2 decimal places
      currency: amount.currency.toUpperCase(),
      confidence: Math.round(amount.confidence * 100) / 100 // Round confidence to 2 decimal places
    };
  }

  /**
   * Generate recommendations based on issues
   */
  private generateRecommendations(issues: string[]): string[] {
    const recommendations: string[] = [];

    if (issues.some(issue => issue.includes('rental income'))) {
      recommendations.push('Update rental income with current contract data');
    }

    if (issues.some(issue => issue.includes('estimated'))) {
      recommendations.push('Replace estimated values with actual data when available');
    }

    if (issues.some(issue => issue.includes('90 days'))) {
      recommendations.push('Update property data to ensure accuracy');
    }

    if (issues.some(issue => issue.includes('property tax'))) {
      recommendations.push('Add property tax information for more accurate calculations');
    }

    if (issues.some(issue => issue.includes('insurance'))) {
      recommendations.push('Include insurance costs for complete expense tracking');
    }

    if (issues.some(issue => issue.includes('growth rate'))) {
      recommendations.push('Define growth rate assumptions for projections');
    }

    if (issues.some(issue => issue.includes('valuation'))) {
      recommendations.push('Obtain current property valuation for accurate metrics');
    }

    return recommendations;
  }

  /**
   * Validate multiple temporal amounts
   */
  validateMultipleAmounts(amounts: TemporalFinancialAmount[]): ValidationResult {
    const allErrors: string[] = [];
    const allWarnings: string[] = [];

    amounts.forEach((amount, index) => {
      const result = this.validateTemporalAmount(amount);
      
      result.errors.forEach(error => {
        allErrors.push(`Amount ${index + 1}: ${error}`);
      });

      result.warnings.forEach(warning => {
        allWarnings.push(`Amount ${index + 1}: ${warning}`);
      });
    });

    return {
      isValid: allErrors.length === 0,
      errors: allErrors,
      warnings: allWarnings
    };
  }

  /**
   * Check currency consistency across amounts
   */
  validateCurrencyConsistency(amounts: TemporalFinancialAmount[]): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (amounts.length === 0) {
      return { isValid: true, errors: [], warnings: [] };
    }

    const baseCurrency = amounts[0].currency;
    const inconsistentCurrencies = amounts
      .filter(amount => amount.currency !== baseCurrency)
      .map(amount => amount.currency);

    if (inconsistentCurrencies.length > 0) {
      errors.push(`Currency inconsistency detected. Base: ${baseCurrency}, Found: ${inconsistentCurrencies.join(', ')}`);
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }
} 